import { ResponsiveDialog } from '@components/Dialogs/ResponsiveDialog';
import { ProjectDocumentsContext } from '@modules/folders/components/ProjectDocumentsContextProvider';
import { DialogProps } from '@mui/material';
import { useNotification } from '@utils/useNotification';
import { useQueryInvalidator } from '@utils/useQueryInvalidator';
import { useResponsive } from '@utils/useResponsive';
import { SignDocumentError, useFolderEntriesQuery, useGetDocumentQuery, useMyTasksQuery, useProjectDocumentsQuery, useProjectTasksQuery, useWorkflowInstancesQuery } from 'gql/index';
import { useContext } from 'react';
import { useIntl } from 'react-intl';
import { ZohoSignaturePad } from './ZohoSignaturePad';

interface OwnProps extends Omit<DialogProps, 'onError'> {
  documentId: number;
  projectId: number;
  requestId?: string;
  actionId?: string;
  onSuccess?: () => void;
  onError?: (error: SignDocumentError[]) => void;
}

export const SignatureDialog: React.FC<OwnProps> = ({ documentId, projectId, requestId, actionId, onSuccess, onError, ...dialogProps }) => {
  const { isMobile } = useResponsive();
  const { formatMessage } = useIntl();
  const invalidateQuery = useQueryInvalidator();
  const { notifySuccess } = useNotification();
  const { currentFolderId } = useContext(ProjectDocumentsContext);

  const invalidateQueriesOnSignatureRequestUpdate = () => {
    invalidateQuery(useGetDocumentQuery, { id: documentId });
    invalidateQuery(useProjectDocumentsQuery, { projectId });
    invalidateQuery(useProjectTasksQuery, { projectId });
    invalidateQuery(useFolderEntriesQuery, { projectId, folderId: currentFolderId });
    invalidateQuery(useWorkflowInstancesQuery, { projectId });
    invalidateQuery(useMyTasksQuery, { projectId });
  };

  const handleClose = () => {
    invalidateQueriesOnSignatureRequestUpdate();
    dialogProps.onClose?.({}, 'backdropClick');
  };

  return (
    <ResponsiveDialog
      maxWidth={'xl'}
      title={isMobile ? formatMessage({ id: 'Sign document' }) : undefined}
      PaperProps={{ sx: { height: '100%', maxHeight: 700 } }}
      {...dialogProps}
      onClose={(event, reason) => {
        dialogProps.onClose?.(event, reason);
      }}
    >
      {actionId && requestId && dialogProps.open &&(
        <ZohoSignaturePad
          requestId={requestId}
          actionId={actionId}
          documentId={documentId}
          onClose={handleClose}
          onSignatureComplete={(status) => {
            if (status === 'Signed') {
              notifySuccess(formatMessage({ id: 'Document signed successfully.' }));
            }
            invalidateQueriesOnSignatureRequestUpdate();
            onSuccess?.();
          }}
        />
      )}
    </ResponsiveDialog>
  );
};